import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { DataTable } from "../../../components/Common/DataTable";

// Custom triangular spike shape for bars (similar to recharts CustomShapeBarChart example)
const getTrianglePath = (x, y, width, height) => {
  // Creates a curved triangular spike shape pointing upward
  return `M${x},${y + height}
          C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3}
          ${x + width / 2}, ${y}
          C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${x + width}, ${y + height}
          Z`;
};

const TriangleBar = (props) => {
  const { fill, x, y, width, height } = props;
  return (
    <path
      d={getTrianglePath(Number(x), Number(y), Number(width), Number(height))}
      stroke="none"
      fill={fill}
    />
  );
};

const parseEntities = (alerts = []) => {
  const collected = [];
  alerts.forEach((alert) => {
    const raw = alert.entities || alert.Entities;
    let list = raw;
    if (typeof raw === "string") {
      try {
        list = JSON.parse(raw);
      } catch {
        list = [];
      }
    }
    if (!Array.isArray(list)) return;
    list.forEach((entity) => {
      const type = (entity.Type || entity.type || "").toString().toLowerCase();
      const address = entity.Address || entity.IpAddress || entity.IP || entity.ip;
      const host =
        entity.HostName || entity.DeviceName || entity.NetBiosName || entity.Machine || entity.Name;
      const account = entity.UserName || entity.Account || entity.PrincipalName || entity.Name;
      const resource = entity.ResourceId || entity.AzureResourceId || entity.id;
      const label =
        address ||
        host ||
        account ||
        resource ||
        entity.Url ||
        entity.FileHash ||
        entity.FileName ||
        "Entity";

      collected.push({
        type,
        label,
        raw: entity,
        alertSeverity: alert.alertSeverity || alert.severity || "medium",
        alertName: alert.displayName || alert.alertName || alert.name,
        time: alert.timeGenerated || alert.startTime || alert.endTime,
      });
    });
  });

  return collected;
};

const normalizeType = (type) => {
  if (!type) return "other";
  const t = type.toLowerCase();
  if (t.includes("ip")) return "ip";
  if (t.includes("host")) return "host";
  if (t.includes("device") || t.includes("machine")) return "host";
  if (t.includes("account") || t.includes("user")) return "account";
  if (t.includes("url")) return "url";
  if (t.includes("file")) return "file";
  if (t.includes("azure")) return "resource";
  return t;
};

const severityClass = (sev) => {
  const s = (sev || "").toString().toLowerCase();
  if (s.includes("crit")) return "badge badge--critical";
  if (s.includes("high")) return "badge badge--high";
  if (s.includes("med")) return "badge badge--medium";
  if (s.includes("low")) return "badge badge--low";
  return "badge";
};

export default function Indicators({ incident }) {
  const alerts = incident?.alerts || incident?._raw?.alerts || [];

  const entities = useMemo(() => parseEntities(alerts), [alerts]);

  const uniqueEntities = useMemo(() => {
    const seen = new Set();
    return entities
      .map((e) => ({
        ...e,
        normType: normalizeType(e.type),
        dedupKey: `${normalizeType(e.type)}|${e.label}`,
      }))
      .filter((e) => {
        if (seen.has(e.dedupKey)) return false;
        seen.add(e.dedupKey);
        return true;
      });
  }, [entities]);

  const grouped = useMemo(() => {
    const map = {};
    uniqueEntities.forEach((e) => {
      const key = e.normType;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [uniqueEntities]);

  const order = ["ip", "host", "account", "url", "file", "resource", "other"];

  const pieData = Object.entries(grouped)
    .map(([key, list]) => ({ name: key.toUpperCase(), value: list.length }))
    .filter((d) => d.value > 0);
  console.log(alerts)
  const alertsBar = alerts.map((a, idx) => {
    const ents = parseEntities([a]).map((e) => ({
      ...e,
      normType: normalizeType(e.type),
      dedupKey: `${normalizeType(e.type)}|${e.label}`,
    }));
    const uniq = Array.from(new Set(ents.map((e) => e.dedupKey)));
    return {
      name: a.displayName || a.alertName || a.name || `Alert ${idx + 1}`,
      entities: uniq.length,
    };
  });

  const colors = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#22d3ee", "#94a3b8"];

  // Flatten entities for table data
  const tableData = useMemo(() => {
    const flatData = [];
    order
      .filter((k) => grouped[k]?.length)
      .forEach((key) => {
        grouped[key].forEach((e) => {
          flatData.push({
            id: `${key}-${e.dedupKey}`,
            type: key,
            value: e.label,
            severity: e.alertSeverity || "unknown",
            alertName: e.alertName || "-",
            time: e.time ? new Date(e.time).toLocaleString() : "-",
            raw: e,
          });
        });
      });
    return flatData;
  }, [grouped, order]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "type",
        header: "Type",
        size: 120,
        cell: ({ getValue }) => (
          <span className="chip chip--muted">{getValue()?.toUpperCase() || "-"}</span>
        ),
      },
      {
        accessorKey: "value",
        header: "Value",
        size: 300,
        cell: ({ getValue }) => (
          <span className="font-mono" style={{ wordBreak: "break-all" }}>
            {getValue() || "-"}
          </span>
        ),
      },
      {
        accessorKey: "severity",
        header: "Severity",
        size: 130,
        cell: ({ getValue }) => {
          const severity = getValue();
          return <span className={severityClass(severity)}>{severity}</span>;
        },
      },
      {
        accessorKey: "alertName",
        header: "Alert Name",
        size: 300,
        cell: ({ getValue }) => (
          <span className="text-muted">{getValue() || "-"}</span>
        ),
      },
      {
        accessorKey: "time",
        header: "Time",
        size: 200,
        cell: ({ getValue }) => (
          <span className="text-muted small">{getValue() || "-"}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="indicators">
      {/* Wrapper for Entities per Alert and Entity Types side by side */}
      <div className="indicators__charts-row">
        <div className="indicators__section">
          <h3 className="indicators__section-title">Entity Types</h3>
          {pieData.length ? (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="pie-chart-legend">
                {pieData.map((item, index) => (
                  <div key={index} className="legend-item">
                    <div
                      className="legend-item__color"
                      style={{ backgroundColor: colors[index % colors.length] }}
                    ></div>
                    <span className="legend-item__label">
                      {item.name} ({item.value})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-muted">No entity type distribution.</div>
          )}
        </div>

      <div className="indicators__section">
        <h3 className="indicators__section-title">Entities per Alert</h3>
        {alertsBar.length ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={alertsBar}
                margin={{ top: 30, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #1f2937)" />
                <XAxis
                  dataKey="name"
                  stroke="var(--text-secondary, #94a3b8)"
                  tick={{ fontSize: 11, fill: "var(--text-secondary, #94a3b8)" }}
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={60}
                />
                <YAxis
                  stroke="var(--text-secondary, #94a3b8)"
                  tick={{ fontSize: 12, fill: "var(--text-secondary, #94a3b8)" }}
                  allowDecimals={false}
                  domain={[0, "auto"]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--bg-secondary, #1f2937)",
                    border: "1px solid var(--border-color, #374151)",
                    borderRadius: "8px",
                    color: "var(--text-primary, #fff)",
                  }}
                />
                <Bar
                  dataKey="entities"
                  fill="#4f46e5"
                  shape={<TriangleBar />}
                  label={{ position: "top", fill: "var(--text-secondary, #94a3b8)", fontSize: 12 }}
                >
                  {alertsBar.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-muted">No alert/entity counts available.</div>
        )}
      </div>
    </div>

      {/* Indicators Table */}
      <div className="indicators__section indicators__table-section">
        <h3 className="indicators__section-title">All Indicators</h3>
        {tableData.length > 0 ? (
          <DataTable
            data={tableData}
            columns={columns}
            searchable={true}
            searchPlaceholder="Search indicators..."
          />
        ) : (
          <div className="text-muted">No indicators found.</div>
        )}
      </div>
    </div>
  );
}
